import React from 'react';
import ReactMarkdown from 'react-markdown';

// IMPORTANT: API Key Management
// The API key should be stored securely, for example, in environment variables
// and accessed via a backend proxy. Hardcoding keys in client-side code is insecure.
// For this example, we'll use a placeholder. Replace with your actual key management strategy.
const GEMINI_API_KEY = 'AIzaSyBkEOZfXdysxHgXIm1sbNOredaEB8FAl5g'; // Replace with your secure key or proxy solution

// Helper function for Gemini API call
async function callGeminiAPI(prompt, userInfo = null) {
    const GEMINI_API_URL = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${GEMINI_API_KEY}`;
    try {
        // Add user context to the prompt if available
        let enhancedPrompt = prompt;
        if (userInfo) {
            const userContext = `User context: Email: ${userInfo.email || 'unknown'}, Role: ${userInfo.role || 'user'}, ID: ${userInfo.id || 'unknown'}.`;
            console.log('Adding user context to Sequoia AI:', userContext);
            enhancedPrompt = `${userContext}\n\n${prompt}`;
        }

        const response = await fetch(GEMINI_API_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                contents: [{
                    parts: [{ text: enhancedPrompt }]
                }]
            })
        });

        if (!response.ok) {
            const errorData = await response.json();
            const message = errorData?.error?.message || `Error connecting to the Gemini API (Status: ${response.status}).`;
            return `I'm sorry, there was an issue with the AI service: ${message} Please try again.`;
        }

        const data = await response.json();
        if (data.candidates && data.candidates[0] && data.candidates[0].content && data.candidates[0].content.parts && data.candidates[0].content.parts[0]) {
            return data.candidates[0].content.parts[0].text;
        } else {
            if (data.promptFeedback && data.promptFeedback.blockReason) {
                return `I'm sorry, your request was blocked. Reason: ${data.promptFeedback.blockReason}. Please rephrase your question.`;
            }
            return "I'm sorry, I received an unexpected or empty response from the AI. Please try again.";
        }
    } catch (error) {
        return "I'm sorry, I couldn't process your request due to a network or unexpected error. Please check your connection and try again.";
    }
}

function LLMChatInterface({ user, teacherPrompt = null, teacherName = null, onBack = null }) {
    try {
        const [messages, setMessages] = React.useState([
            {
                role: 'assistant',
                content: "Hello! I'm the Sequoia A.I. Assistant, powered by Google Gemini. I can answer your questions about sustainability, technology, and environmental topics."
            },
            {
                role: 'system',
                content: "🌿 Sequoia Green AI is coming soon! Our sustainable AI model will be fully powered by renewable energy."
            }
        ]);
        const [input, setInput] = React.useState('');
        const [isLoading, setIsLoading] = React.useState(false);
        const [showSignup, setShowSignup] = React.useState(false);
        const [signupEmail, setSignupEmail] = React.useState("");
        const [signupStatus, setSignupStatus] = React.useState("");
        const [signupLoading, setSignupLoading] = React.useState(false);
        const [signupError, setSignupError] = React.useState("");
        const [expandPromo, setExpandPromo] = React.useState(false);
        const messagesEndRef = React.useRef(null);
        const chatContainerRef = React.useRef(null);

        const scrollToBottom = () => {
            if (chatContainerRef.current) {
                const { scrollHeight, clientHeight } = chatContainerRef.current;
                const maxScroll = scrollHeight - clientHeight;
                
                chatContainerRef.current.scrollTo({
                    top: maxScroll,
                    behavior: 'smooth'
                });
            }
        };

        React.useEffect(() => {
            if (messages.length > 0) {
                scrollToBottom();
            }
        }, [messages]);

        React.useEffect(() => {
            if (teacherName) {
                // Show only teacher name to user, hide prompt injection
                setMessages([{   
                    role: 'system',
                    content: `You are now chatting with the ${teacherName}.`
                }]);
            }
        }, [teacherName]);

        const suggestions = [
            "How does renewable energy work?",
            "Explain climate change in simple terms",
            "What is sustainable computing?",
            "How can I reduce my carbon footprint?",
            "Tell me about solar power",
            "How does e-waste impact the environment?"
        ];

        const handleInputChange = (e) => {
            setInput(e.target.value);
        };

        const handleSuggestionClick = (suggestion) => {
            setInput(suggestion);
        };

        const handleSubmit = async (e) => {
            e.preventDefault();
            if (!input.trim()) return;

            const userMessage = { role: 'user', content: input };
            setMessages(prev => [...prev, userMessage]);
            setInput('');
            setIsLoading(true);

            try {
                // Prepend teacherPrompt internally for API context, but keep UI clean
                const apiPrompt = teacherPrompt ? `${teacherPrompt} \n\n${input}` : input;
                console.log('Sending message to Sequoia AI:', { 
                    hasUserObject: !!user,
                    userEmail: user?.email,
                    userRole: user?.role 
                });
                const response = await callGeminiAPI(apiPrompt, user);
                requestAnimationFrame(() => {
                    setMessages(prev => [...prev, { role: 'assistant', content: response }]);
                    setIsLoading(false);
                });
            } catch (error) {
                console.error('Error getting AI response:', error);
                requestAnimationFrame(() => {
                    setMessages(prev => [...prev, {
                        role: 'assistant',
                        content: 'Sorry, I encountered an error connecting to the AI service. Please try again.'
                    }]);
                    setIsLoading(false);
                });
            }
        };

        const validateEmail = (email) => {
            return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
        };

        const handleSignupClick = () => {
            setShowSignup(true);
            setSignupStatus("");
            setSignupError("");
            setSignupEmail("");
        };

        const handleSignupSubmit = async (e) => {
            e.preventDefault();
            setSignupError("");
            setSignupStatus("");
            
            if (!validateEmail(signupEmail)) {
                setSignupError("Please enter a valid email address.");
                return;
            }
            
            setSignupLoading(true);
            try {
                await window.signupService.addEarlyAccessSignup(signupEmail);
                setSignupStatus("Thank you! You'll be one of the first to experience Sequoia Green AI.");
                setSignupEmail("");
                setTimeout(() => setShowSignup(false), 3000);
            } catch (err) {
                console.error('Signup error:', err);
                setSignupError("There was a problem saving your email. Please try again later.");
            } finally {
                setSignupLoading(false);
            }
        };

        return (
            <div className="flex flex-col h-full bg-gray-900/60 backdrop-blur-md">
                {teacherName && (
                    <div className="px-6 py-3 bg-gray-800/70 border-b border-gray-700 flex items-center">
                        <button onClick={onBack} className="text-green-400 hover:text-green-300 mr-4">
                            <i className="fas fa-arrow-left"></i>
                        </button>
                        <h3 className="text-white font-medium">{teacherName}</h3>
                    </div>
                )}
                {/* Header with Status */}
                <div className="bg-gray-800/70 px-6 py-3 border-b border-gray-700 flex justify-between items-center">
                    <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-green-500 to-emerald-600 flex items-center justify-center shadow-lg mr-3">
                            <i className="fas fa-robot text-white"></i>
                        </div>
                        <div>
                            <h3 className="font-medium text-white">Sequoia A.I. Chat</h3>
                            <p className="text-xs text-gray-400">Currently powered by Google Gemini</p>
                        </div>
                    </div>
                </div>





                {/* Chat Messages */}
                <div 
                    ref={chatContainerRef}
                    className="flex-1 overflow-y-auto px-4 md:px-8 py-6 space-y-6"
                >
                    {messages.map((message, index) => (
                        <div 
                            key={index}
                            className={`flex ${
                                message.role === 'user' 
                                    ? 'justify-end' 
                                    : message.role === 'system'
                                        ? 'justify-center'
                                        : 'justify-start'
                            } animate-fadeIn`}
                            style={{animationDelay: `${index * 0.1}s`}}
                        >
                            {message.role === 'system' ? (
                                <div className="bg-green-500/20 text-green-300 text-sm py-2 px-4 rounded-full border border-green-500/30 flex items-center gap-2">
                                    <i className="fas fa-leaf"></i>
                                    {message.content}
                                </div>
                            ) : message.role === 'user' ? (
                                <div className="flex items-start gap-3 max-w-[80%]">
                                    <div className="bg-green-600 text-white rounded-2xl rounded-tr-none p-4 shadow-md order-2">
                                        {message.content}
                                    </div>
                                    <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center text-white text-xs order-1">
                                        <i className="fas fa-user"></i>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-start gap-3 max-w-[80%]">
                                    <div className="bg-white text-gray-900 rounded-2xl p-4 shadow-md order-2 prose prose-sm prose-invert">
                                        <ReactMarkdown>{message.content}</ReactMarkdown>
                                    </div>
                                    <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center text-gray-600 text-xs order-1">
                                        <i className="fas fa-robot"></i>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                    {isLoading && (
                        <div className="flex justify-start">
                            <div className="flex items-start gap-3 max-w-[80%]">
                                <div className="w-8 h-8 bg-gradient-to-tr from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-md">
                                    <i className="fas fa-robot text-white text-xs"></i>
                                </div>
                                <div className="bg-gray-800/70 text-gray-100 rounded-2xl rounded-tl-none p-4 shadow-md">
                                    <div className="flex space-x-2 items-center px-4">
                                        <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                                        <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse delay-150"></div>
                                        <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse delay-300"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>

                {/* Chat Input */}
                <div className="border-t border-gray-800 px-4 md:px-8 py-4 bg-gray-800/40">
                    <form onSubmit={handleSubmit} className="flex items-end gap-2">
                        <div className="relative flex-1">
                            <textarea
                                value={input}
                                onChange={handleInputChange}
                                placeholder="Ask something about sustainability or technology..."
                                className="w-full bg-gray-700/60 border border-gray-600/50 rounded-xl px-4 py-3 pr-12 text-white resize-none focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                rows={1}
                                style={{
                                    minHeight: '60px',
                                    maxHeight: '140px'
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSubmit(e);
                                    }
                                }}
                                disabled={isLoading}
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={isLoading || !input.trim()}
                            className={`p-3 rounded-full flex items-center justify-center shadow-lg ${
                                isLoading || !input.trim()
                                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                    : 'bg-gradient-to-tr from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 text-white'
                            } transition-all duration-300`}
                        >
                            <i className="fas fa-paper-plane"></i>
                        </button>
                    </form>
                </div>

                <style jsx>{`
                    @keyframes fadeIn {
                        from { opacity: 0; transform: translateY(10px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    .animate-fadeIn {
                        animation: fadeIn 0.3s ease-out forwards;
                    }
                    @keyframes pulse-green {
                        0%, 100% { opacity: 1; }
                        50% { opacity: 0.6; }
                    }
                    .animate-pulse-green {
                        animation: pulse-green 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
                    }
                `}</style>
            </div>
        );
    } catch (error) {
        console.error('LLMChatInterface error:', error);
        return null;
    }
}

window.LLMChatInterface = LLMChatInterface;
export default LLMChatInterface;
